import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Layout, PanelLeft, PanelLeftClose, Plus, X, RefreshCw, Save, ArrowLeft } from 'lucide-react'
import Sidebar from './Sidebar'
import NotesPage from '../../pages/notes/NotesPage'
import { TabsProvider, useTabs } from '../../contexts/TabsContext'
import { useFolders } from '../../contexts/FoldersContext'
import { useNotes } from '../../contexts/NotesContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useCloudStorage } from '../../hooks/useCloudStorage'
import TabBar from './TabBar'
import TabContent from './TabContent'
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts'
import KeyboardShortcutsHelp from '../common/KeyboardShortcutsHelp'
import { useStatus } from '../../contexts/StatusContext'

/**
 * Inner component that has access to tabs context
 */
const NotesLayoutInner = ({ isSidebarOpen, onSidebarToggle }) => {
  const location = useLocation()
  const { tabs, activeTabId, closeTab, openTab, switchToTab, cycleToNextTab, cycleToPreviousTab } = useTabs()
  const { cycleToNextFolder, cycleToPreviousFolder, currentFolder } = useFolders()
  const { retryInitialSync, loading, testGoogleDriveConnection, handleGoogleDriveTokenExpiration } = useNotes()
  const { isLocalMode } = useLocalMode()
  const { isConnected, loading: cloudLoading } = useCloudStorage()
  const { showLoading, showSuccess, showError, showWarning, dismissStatus } = useStatus()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showMobileNotesList, setShowMobileNotesList] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isNotesListVisible, setIsNotesListVisible] = useState(true)
  const [touchStartX, setTouchStartX] = useState(0)
  const [touchEndX, setTouchEndX] = useState(0)
  const [isMobileView, setIsMobileView] = useState(false)

  /**
   * Handle creating a new note
   */
  const handleCreateNote = () => {
    // Generate a unique ID for each new note tab
    const uniqueId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Count existing new note tabs to create unique title
    const newNoteCount = tabs.filter(tab => tab.type === 'new').length + 1
    const uniqueTitle = `New Note ${newNoteCount}`
    
    // If in sensitive folder, create note in All Notes (no specific folder)
    const noteData = currentFolder?.folderType === 'SENSITIVE' ? { folderId: null } : null
    
    openTab({
      type: 'new',
      noteId: uniqueId,
      title: uniqueTitle,
      note: noteData
    })
    
      // In mobile view, switch to tab content when creating a note
  setShowMobileNotesList(false)
  }

  /**
   * Handle imported notes - open them in new tabs
   */
  const handleNotesImported = (importedNotes) => {
    console.log('NotesLayout: handleNotesImported called with:', importedNotes)
    try {
      for (const importedNote of importedNotes) {
        // Generate unique ID for temporary note
        const uniqueId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        
        // Create temporary note object for tab (not saved to storage)
        const tempNote = {
          id: uniqueId,
          title: importedNote.title,
          content: importedNote.content,
          isTemporary: true,
          isUnsaved: true,
          // Add metadata about import
          metadata: {
            imported: true,
            originalFilename: importedNote.originalFilename,
            fileType: importedNote.fileType,
            importDate: importedNote.importDate
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        // Open in new tab
        openTab({
          type: 'new',
          noteId: uniqueId,
          title: importedNote.title,
          note: tempNote,
          isExpanded: false
        })
      }
      
      // In mobile view, switch to tab content when importing files
      if (isMobileView) {
        setShowMobileNotesList(false)
      }
      
    } catch (error) {
      console.error('Failed to open imported notes:', error)
    }
  }

  /**
   * Handle mobile save action - trigger form submission
   */
  const handleMobileSave = () => {
    // Dispatch a custom event that the edit/create pages can listen to
    window.dispatchEvent(new CustomEvent('mobile-save-trigger'))
  }

  /**
   * Handle mobile cancel action - close current tab or navigate back
   */
  const handleMobileCancel = () => {
    if (activeTabId) {
      closeTab(activeTabId)
    } else {
      // If no active tab, navigate to notes list
      setShowMobileNotesList(true)
    }
  }

  // Check if we're on edit/create pages
  const isEditOrCreatePage = location.pathname.includes('/notes/edit/') || 
                            location.pathname.includes('/notes/new') ||
                            (activeTabId && (tabs.find(tab => tab.id === activeTabId)?.type === 'edit' || 
                                            tabs.find(tab => tab.id === activeTabId)?.type === 'new'))

  /**
   * Handle manual sync button click with enhanced authentication detection
   */
  const handleManualSync = async () => {
  if (isSyncing || loading) return // Prevent multiple clicks
  
  setIsSyncing(true)
  try {
    console.log('🔄 NotesLayout: Manual sync triggered')
    
    // First test the connection to detect auth issues early
    const testingId = showLoading('Testing Google Drive connection...')
    
    try {
      await testGoogleDriveConnection()
      console.log('✅ NotesLayout: Connection test passed, proceeding with sync')
    } catch (connectionError) {
      console.log('⚠️ NotesLayout: Connection test failed, checking for auth issues')
      
      // Check if it's an authentication error
      if (connectionError.response?.status === 401 || 
          connectionError.message?.includes('No cloud provider configured') ||
          connectionError.message?.includes('TOKEN_EXPIRED')) {
        
        console.log('🔄 NotesLayout: Authentication issue detected, triggering re-auth')
        dismissStatus(testingId)
        showError('Google Drive connection lost. Opening reconnection...')
        
        // Trigger re-authentication flow
        handleGoogleDriveTokenExpiration()
        return // Don't proceed with sync
      }
    }
    
    dismissStatus(testingId)
    const syncingId = showLoading('🔄 Syncing notes...')
    
    const result = await retryInitialSync()
    
    if (result === false) {
      // Auth issue was detected and handled
      dismissStatus(syncingId)
      return
    }
    
    dismissStatus(syncingId)
    showSuccess('✅ Sync completed!')
    
  } catch (error) {
    console.error('❌ NotesLayout: Manual sync failed:', error)
    showError('❌ Sync failed - please try again')
  } finally {
    // Reset syncing state immediately when sync actually completes
    setIsSyncing(false)
  }
}


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  const toggleNotesListVisibility = () => {
    setIsNotesListVisible(!isNotesListVisible)
  }

  /**
   * Handle touch events for mobile swipe navigation
   */
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return
    
    const distance = touchStartX - touchEndX
    const isLeftSwipe = distance > 400 // Swipe left (show note content)
    const isRightSwipe = distance < -400 // Swipe right (show notes list)
    
    if (isLeftSwipe) {
    //if (isLeftSwipe && showMobileNotesList && tabs.length > 0) {
      console.log('👆 Mobile: Swipe left detected - showing note content')
      setShowMobileNotesList(false)
    }

    if (isLeftSwipe && !showMobileNotesList) {
      //if (isLeftSwipe && showMobileNotesList && tabs.length > 0) {
        console.log('👆 Mobile: Swipe left detected - showing note content')
        setShowMobileNotesList(true)
    }
    
    //if (isRightSwipe && !showMobileNotesList) {
    if (isRightSwipe) {
      console.log('👆 Mobile: Swipe right detected - showing notes list')
      setShowMobileNotesList(true)
    }
    
    // Reset touch values
    setTouchStartX(0)
    setTouchEndX(0)
  }

  // Effect to automatically show notes list when no tabs are open
  useEffect(() => {
    if (tabs.length === 0) {
      setShowMobileNotesList(true)
    }
  }, [tabs.length])

  // Effect to detect mobile vs desktop view
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }
    
    checkViewport() // Check on mount
    window.addEventListener('resize', checkViewport)
    
    return () => window.removeEventListener('resize', checkViewport)
  }, [])

  // Android back button/gesture handling for mobile navigation
  useEffect(() => {
    // Only handle back navigation on mobile (screen width < 768px)
    const isMobileView = () => window.innerWidth < 768
    
    const handlePopState = (event) => {
      if (!isMobileView()) return
      
      const state = event.state
      console.log('📱 Back navigation detected:', state)
      
      // If we're coming back to notes list from note content
      if (state?.view === 'notesList' && !showMobileNotesList) {
        console.log('📱 Android back gesture: Returning to notes list')
        setShowMobileNotesList(true)
      }
      // Fallback: if no state but we're showing note content, assume back to notes list
      else if (!state && !showMobileNotesList && tabs.length > 0) {
        console.log('📱 Android back gesture (fallback): Returning to notes list')
        setShowMobileNotesList(true)
      }
      // If no state or already on notes list, allow default behavior (close app)
    }
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', handlePopState)
    
    // Set initial state when component mounts
    if (isMobileView()) {
      if (showMobileNotesList) {
        window.history.replaceState({ view: 'notesList' }, '', '')
      } else {
        // If starting with a note open, ensure we have proper history
        window.history.replaceState({ view: 'noteContent' }, '', '')
      }
    }
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, []) // Run only on mount

  // Manage history state when mobile view changes
  useEffect(() => {
    const isMobileView = () => window.innerWidth < 768
    
    if (!isMobileView()) return
    
    if (!showMobileNotesList && tabs.length > 0) {
      // Navigating to note content - push new state so back gesture works
      console.log('📱 Navigating to note content: Pushing history state')
      window.history.pushState({ view: 'noteContent' }, '', '')
    }
    // Don't need to handle the reverse case as popstate will handle it
  }, [showMobileNotesList, tabs.length])

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'ctrl+shift+n': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Shift+N - Creating new note')
      handleCreateNote()
      showSuccess('📝 New note created!')
    },
    'ctrl+shift+arrowright': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Shift+Right - Next note tab')
      cycleToNextTab()
    },
    'ctrl+shift+arrowleft': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Shift+Left - Previous note tab')
      cycleToPreviousTab()
    },
    'ctrl+alt+arrowright': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Alt+Right - Next folder')
      cycleToNextFolder()
    },
    'ctrl+alt+arrowleft': (event) => {
      console.log('🎹 Keyboard shortcut: Ctrl+Alt+Left - Previous folder')
      cycleToPreviousFolder()
    }
  }, [handleCreateNote, cycleToNextTab, cycleToPreviousTab, cycleToNextFolder, cycleToPreviousFolder])

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-800 overflow-x-hidden">
      <div className="flex h-full w-full max-w-full overflow-x-hidden">
        {/* Left Sidebar - Desktop Only */}
        <div className={`
          transition-all duration-300 ease-in-out flex-shrink-0 h-full
          ${isSidebarOpen ? 'w-64' : 'w-0'}
          hidden md:block
        `}>
          <div className={`w-64 h-full ${isSidebarOpen ? 'block' : 'hidden'}`}>
            <Sidebar 
              isOpen={true}
              onClose={() => {}}
              onNotesImported={handleNotesImported}
            />
          </div>
        </div>

        {/* Mobile Sidebar - Only visible on mobile */}
        <div className="md:hidden">
          <Sidebar 
            isOpen={isMobileMenuOpen}
            onClose={closeMobileMenu}
            onNotesImported={handleNotesImported}
          />
        </div>

        {/* Mobile Layout - Simplified Single Column */}
        {isMobileView && (
        <div className="flex-1 h-full flex flex-col bg-white dark:bg-gray-800 mobile-container">
          {/* Simplified Mobile Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            {!showMobileNotesList && tabs.length > 0 ? (
              <>
                <button
                  onClick={() => setShowMobileNotesList(true)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full"
                  title="Back to notes"
                >
                  <PanelLeftClose className="h-5 w-5" />
                </button>
                <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100 truncate mx-3 flex-1 text-center">
                  {tabs.find(tab => tab.id === activeTabId)?.title || 'Note'}
                </h1>
                <div className="flex items-center space-x-1">
                  {isEditOrCreatePage && (
                    <>
                      <button
                        onClick={handleMobileSave}
                        className="p-1.5 text-indigo-600 dark:text-indigo-400 rounded-full"
                        title="Save"
                      >
                        <Save className="h-5 w-5" />
                      </button>
                      <button
                        onClick={handleMobileCancel}
                        className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full"
                        title="Cancel"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleCreateNote}
                    className="p-1.5 text-indigo-600 dark:text-indigo-400 rounded-full"
                    title="New note"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={toggleMobileMenu}
                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full"
                    title="Menu"
                  >
                    <PanelLeft className="h-5 w-5" />
                  </button>
                  <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100">All Notes</h1>
                </div>
                <div className="flex items-center space-x-2">
                  {!isLocalMode && (
                    <>
                      <span
                        className={`w-2 h-2 rounded-full ${
                          cloudLoading ? 'bg-gray-300' : isConnected ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        title={cloudLoading ? 'Connecting…' : (isConnected ? 'Connected' : 'Disconnected')}
                      />
                      <button
                        onClick={handleManualSync}
                        disabled={isSyncing || loading}
                        className={`p-1.5 rounded-full ${
                          isSyncing ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:text-blue-600'
                        }`}
                        title={isSyncing ? 'Syncing...' : 'Sync'}
                      >
                        <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleCreateNote}
                    className="p-1.5 text-indigo-600 dark:text-indigo-400 rounded-full"
                    title="New note"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile Content Area - Fixed Sliding */}
          <div className="flex-1 overflow-hidden relative w-full">
            <div 
              className={`flex h-full transition-transform duration-300 ease-out ${
                showMobileNotesList ? 'translate-x-0' : '-translate-x-full'
              }`}
              style={{ width: '200vw' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Notes List Panel - Left */}
              <div className="h-full overflow-y-auto flex-shrink-0" style={{ width: '100vw' }}>
                <div className={tabs.length > 0 ? 'pb-14' : ''}>
                  <NotesPage onNoteOpen={() => setShowMobileNotesList(false)} />
                </div>
              </div>
              
              {/* Note Content Panel - Right */}
              <div className="h-full overflow-y-auto flex-shrink-0" style={{ width: '100vw' }}>
                {tabs.length > 0 ? (
                  <div className="pb-14">
                    <TabContent />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full p-8">
                    <div className="text-center">
                      <Layout className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
                        No Note Selected
                      </h3>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Select a note to view it here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Mobile Tab Bar */}
          {tabs.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50 z-50 enhanced-mobile-tab-bar">
              <div className="flex overflow-x-auto px-2 py-1">
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className={`relative flex-shrink-0 mx-1 rounded-3xl transition-all duration-200 ${
                      tab.id === activeTabId 
                        ? 'bg-indigo-500 text-white shadow-md' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    style={{ minWidth: '80px', maxWidth: '140px' }}
                  >
                    <button
                      onClick={() => {
                        // Always switch to the tab (even if it's already active)
                        switchToTab(tab.id)
                        // Always trigger the swiping animation to show note content
                        setShowMobileNotesList(false)
                      }}
                      className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium"
                    >
                      <span className="truncate">
                        {tab.title.length > 12 ? `${tab.title.substring(0, 12)}...` : tab.title}
                      </span>
                      {tab.isDirty && (
                        <div className="w-1.5 h-1.5 bg-amber-400 rounded-full ml-1 flex-shrink-0"></div>
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        closeTab(tab.id)
                        if (tab.id === activeTabId && tabs.length === 1) {
                          setShowMobileNotesList(true)
                        }
                      }}
                      className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs transition-colors ${
                        tab.id === activeTabId 
                          ? 'bg-white text-indigo-500 hover:bg-gray-100' 
                          : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-400 dark:hover:bg-gray-500'
                      }`}
                      title="Close"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                <button
                  onClick={() => setShowMobileNotesList(true)}
                  className="flex-shrink-0 px-3 h-8 mx-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors flex items-center justify-center text-sm font-medium"
                  title="Notes List"
                >
                  <PanelLeft className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Notes</span>
                </button>
                
                <button
                  onClick={handleCreateNote}
                  className="flex-shrink-0 w-12 h-8 mx-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 rounded-lg transition-colors flex items-center justify-center"
                  title="New note"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        )}

                {/* Desktop Layout - 2 or 3 Columns */}
        {!isMobileView && (
        <div className="flex flex-1 min-w-0 max-w-full overflow-x-hidden">
          {/* Middle Column - Notes List */}
          <div className={`
            flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-x-hidden
            transition-all duration-300 ease-in-out relative
            ${isNotesListVisible ? 'w-80 lg:w-96 max-w-96' : 'w-0'}
          `}>
            <div className={`h-full flex flex-col w-80 lg:w-96 ${isNotesListVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              {/* Toggle Button and Header */}
              <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={onSidebarToggle}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={isSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
                  >
                    {isSidebarOpen ? (
                      <PanelLeftClose className="h-5 w-5" />
                    ) : (
                      <PanelLeft className="h-5 w-5" />
                    )}
                  </button>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    All Notes
                  </h2>
                </div>
                {!isLocalMode && (
                  <div className="flex items-center space-x-2">
                    <span
                      className={
                        `inline-block h-2.5 w-2.5 rounded-full border ` +
                        (cloudLoading
                          ? 'bg-white border-gray-300'
                          : isConnected
                            ? 'bg-blue-500 border-blue-500'
                            : 'bg-red-500 border-red-500')
                      }
                      title={cloudLoading ? 'Connecting…' : (isConnected ? 'Connected' : 'Disconnected')}
                    />
                    <button
                      onClick={handleManualSync}
                      disabled={isSyncing || loading}
                      className={`p-2 rounded-full transition-colors flex-shrink-0 ${
                        isSyncing || loading
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      title={isSyncing ? 'Syncing...' : 'Sync notes'}
                    >
                      <RefreshCw className={`h-5 w-5 ${isSyncing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Notes List */}
              <div className="flex-1 overflow-hidden">
                <NotesPage onNoteOpen={() => setShowMobileNotesList(false)} />
              </div>
            </div>
          </div>

          {/* Right Column - Tabbed Interface */}
          <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-800 overflow-x-hidden relative">
            {/* Tab Bar - Always visible (absolutely positioned) */}
            <TabBar 
              onToggleNotesList={toggleNotesListVisibility}
              isNotesListVisible={isNotesListVisible}
            />
            
            {/* Tab Content or Empty State */}
            <div className="flex-1 w-full max-w-full overflow-y-auto overflow-x-hidden pt-0 md:pt-[61px]">
              {tabs.length > 0 ? (
                <TabContent />
              ) : (
                /* Empty state when no tabs */
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center max-w-md">
                    <Layout className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
                      Welcome to Notes
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Click on any note from the list to open it here, or use the + button above to create a new note.
                    </p>
                    <div className="mt-6">
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        💡 Tip: You can have multiple notes open in tabs simultaneously
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />
    </div>
  )
}

/**
 * Notes Layout Component
 * 
 * 3-column layout specifically for the notes section:
 * - Left: Toggleable sidebar with navigation
 * - Middle: Notes list with search and management
 * - Right: Tabbed interface for viewing/editing notes
 */
const NotesLayout = () => {
  // Initialize sidebar state from localStorage or default to true
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarOpen')
      return saved !== null ? JSON.parse(saved) : true
    } catch {
      return true
    }
  })
  const [lastViewportWidth, setLastViewportWidth] = useState(window.innerWidth)

  // Responsive sidebar management - auto-hide when viewport is too narrow
  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth
      const NARROW_VIEWPORT_BREAKPOINT = 1280 // Below this width, auto-hide sidebar
      
      // Only auto-manage sidebar when viewport actually changes size (not on initial load)
      if (Math.abs(viewportWidth - lastViewportWidth) > 50) { // 50px threshold to avoid minor changes
        console.log(`📱 NotesLayout: Viewport changed from ${lastViewportWidth}px to ${viewportWidth}px`)
        
        if (viewportWidth < NARROW_VIEWPORT_BREAKPOINT && isSidebarOpen) {
          console.log(`📱 NotesLayout: Auto-hiding sidebar (viewport: ${viewportWidth}px < ${NARROW_VIEWPORT_BREAKPOINT}px)`)
          setIsSidebarOpen(false)
          localStorage.setItem('sidebarOpen', 'false')
        } else if (viewportWidth >= NARROW_VIEWPORT_BREAKPOINT && !isSidebarOpen) {
          console.log(`🖥️ NotesLayout: Auto-showing sidebar (viewport: ${viewportWidth}px >= ${NARROW_VIEWPORT_BREAKPOINT}px)`)
          setIsSidebarOpen(true)
          localStorage.setItem('sidebarOpen', 'true')
        }
        
        setLastViewportWidth(viewportWidth)
      }
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isSidebarOpen, lastViewportWidth])

  const handleSidebarToggle = () => {
    console.log('👆 NotesLayout: User manually toggled sidebar')
    const newState = !isSidebarOpen
    setIsSidebarOpen(newState)
    
    // Persist user preference in localStorage
    try {
      localStorage.setItem('sidebarOpen', JSON.stringify(newState))
      console.log(`💾 NotesLayout: Saved sidebar state: ${newState}`)
    } catch (error) {
      console.warn('Failed to save sidebar state:', error)
    }
  }

  return (
    <TabsProvider>
      <NotesLayoutInner 
        isSidebarOpen={isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
      />
    </TabsProvider>
  )
}

export default NotesLayout 